import type { DeletionRecord } from '../types';

export const OBSERVATION_ERASURE_WARNING = 'Any kindergarten observation records for this student in this class will be permanently deleted. This cannot be undone, including by restoring the student from History. Other saved student data may still be recoverable under the existing History behavior. Other classes and the school directory are not affected.';
export const CLASS_OBSERVATION_ERASURE_WARNING = 'Any kindergarten observation records in this class will be permanently deleted. This cannot be undone. Restoring the class from History will not recover those observations.';

export function observationErasurePending(record: Pick<DeletionRecord, 'observationErasure'>): boolean {
  return record.observationErasure === 'pending';
}

export function assertHistoryCanBeCleared(records: Pick<DeletionRecord, 'observationErasure'>[]): void {
  if (records.some(observationErasurePending)) throw new Error('Finish pending student deletions before clearing History.');
}

export function assertRecordCanBeRestored(record: Pick<DeletionRecord, 'observationErasure'>): void {
  if (observationErasurePending(record)) throw new Error('Finish this student deletion before restoring. Erased observations cannot be recovered.');
}

type PendingRecord = Omit<DeletionRecord, 'id'>;
/** Preserve the pre-existing roster/history route outside the educator feature.
 * This deliberately makes no observation-erasure claim and cannot bypass a
 * pending observation task. Existing Firestore permissions still authorize it.
 */
export async function deleteStudentWithLegacyHistory(record: PendingRecord, dependencies: {
  saveSnapshot: (record: PendingRecord) => Promise<string>;
  onSaved: (record: DeletionRecord) => void;
  removeStudent: (classId: string, studentId: string) => Promise<void>;
}): Promise<void> {
  if (record.type !== 'student' || !record.context?.classId || typeof record.data?.id !== 'string'
    || !record.data.id || record.observationErasure !== undefined) {
    throw new Error('An observation deletion cannot use the legacy student deletion route.');
  }
  const id = await dependencies.saveSnapshot(record);
  dependencies.onSaved({ ...record, id });
  await dependencies.removeStudent(record.context.classId, record.data.id);
}

interface DeletionDependencies {
  confirm: (message: string) => Promise<boolean>;
  savePending: (record: PendingRecord) => Promise<string>;
  loadPending: (recordId: string) => Promise<DeletionRecord>;
  verifyAccess: (classId: string, studentId: string) => Promise<void>;
  removeStudent: (classId: string, studentId: string) => Promise<void>;
  purgeObservations: (classId: string, studentId: string) => Promise<void>;
  markComplete: (recordId: string) => Promise<void>;
  onPending: (record: DeletionRecord) => void;
  onComplete: (record: DeletionRecord) => void;
}

/** A durable pending marker precedes every mutation; failures leave a retryable History item.
 * Only existing restore data and a state marker go to History, never observation evidence.
 * Retries repeat roster removal and head-based cleanup; no partial step is reported as complete.
 */
export async function deleteStudentWithObservationErasure(
  record: PendingRecord | DeletionRecord,
  dependencies: DeletionDependencies,
): Promise<boolean> {
  const classId = record.context?.classId;
  const studentId = (record.data as { id?: unknown } | undefined)?.id;
  if (record.type !== 'student' || !classId || typeof studentId !== 'string' || !studentId) {
    throw new Error('Invalid student deletion record.');
  }
  const retry = 'id' in record;
  if (retry && !observationErasurePending(record)) throw new Error('This student deletion is already complete.');
  const message = retry
    ? `Retry removing this student from the class and finish deleting their observation records. Some records may already be erased. ${OBSERVATION_ERASURE_WARNING}`
    : OBSERVATION_ERASURE_WARNING;
  if (!(await dependencies.confirm(message))) return false;
  // Recheck after confirmation, which may have been open while another tab finished.
  if ('id' in record) {
    const fresh = await dependencies.loadPending(record.id);
    if (!observationErasurePending(fresh) || fresh.type !== 'student'
      || fresh.context?.classId !== classId || fresh.data?.id !== studentId) {
      throw new Error('This deletion changed in another session. Refresh History before continuing.');
    }
    record = fresh;
  }
  // Refuse unsupported roles/classes before any existing student data is removed.
  await dependencies.verifyAccess(classId, studentId);
  const pending: PendingRecord = { ...record, observationErasure: 'pending' };
  const recordId = 'id' in record ? record.id : await dependencies.savePending(pending);
  const persisted = { ...pending, id: recordId };
  dependencies.onPending(persisted);
  await dependencies.removeStudent(classId, studentId);
  await dependencies.purgeObservations(classId, studentId);
  await dependencies.markComplete(recordId);
  dependencies.onComplete({ ...persisted, observationErasure: 'complete' });
  return true;
}
